import React, { useState, useEffect, useCallback } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import SchoolIcon from '@mui/icons-material/School';
import { alpha } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { Select } from '../select';
import { teachersOnboardingTextFields } from '../../_mock';

export default function AlertDialog({ open, handleClose, addSchool, setSchoolData, schoolData, isLoading }) {
  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm({});
  

  const [previewImage, setPreviewImage] = useState('');
  const [selectedImage, setSelectedImage] = useState('');

  const onDrop = useCallback(
    (acceptedFiles) => {
      const imageFile = acceptedFiles?.[0];
      if (!imageFile) return;

      if (imageFile.size > 10 * 1024 * 1024) {
        toast.error('Image size should not exceed 10MB');
        return;
      }

      setSelectedImage(imageFile);
      setPreviewImage(URL.createObjectURL(imageFile));
    },
    []
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/jpeg': ['.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/jpg': ['.jpg'],
      'image/gif': ['.gif'],
      'image/svg+xml': ['.svg'],
    },
    multiple: false,
    onDrop,
    maxSize: 10 * 1024 * 1024,
  });

  useEffect(() => {
    if (Object.keys(schoolData).length !== 0) {
      addSchool();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolData]);

  const onSubmit = (data) => {
    if (selectedImage && selectedImage.size > 10 * 1024 * 1024) {
      toast.error('Resolve the Errors');
      return;
    }

    const formData = new FormData();

    Object.keys(data).forEach((key) => formData.append(key, data[key]));
    formData.append('image', selectedImage);

    setSchoolData({ formData });
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="add-partnership-site-dialog-title"
        aria-describedby="add-partnership-site-dialog-description"
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 3,
            overflow: 'hidden',
            height: 'max-content',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography id="add-partnership-site-dialog-title" variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Add Partnership Site
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            {/* Image upload area */}
            <Box
              {...getRootProps()}
              sx={{
                mb: 3,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
                px: { xs: 2, sm: 3 },
                py: { xs: 2.5, sm: 3 },
                cursor: 'pointer',
              }}
            >
              <input {...getInputProps()} />
              <Box
                sx={{
                  borderRadius: 2,
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : alpha('#9CA3AF', 0.8),
                  bgcolor: isDragActive ? alpha('#A94B9C', 0.03) : 'background.paper',
                  px: { xs: 2.5, sm: 4 },
                  py: { xs: 3, sm: 4 },
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: alpha('#A94B9C', 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {previewImage ? (
                    <Box
                      component="img"
                      src={previewImage}
                      alt="Partnership site"
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <SchoolIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  )}
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Drag &amp; drop partnership site image
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  or click to browse
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  Supports: JPEG, PNG, WebP, GIF, SVG (Max 10MB)
                </Typography>
              </Box>
            </Box>
            <TextField
              name="name"
              label="Partnership Name"
              placeholder="Partnership Site Here"
              fullWidth
              {...register(
                'name',
                {
                  required: `Partnership Site is required`,
                  maxLength: { value: 125, message: 'School name cannot exceed 125 characters' },
                }
              )}
              error={errors && !!errors?.name}
              helperText={errors?.name?.message}
              type="text"
              sx={{ mb: '17px', '& .MuiFormHelperText-root': { margin: 0 } }}
            />
            {/* <TextField
              name="district"
              label="District / Country"
              placeholder="District / Country Here"
              fullWidth
              type="text"
              sx={{ mb: '10px', '& .MuiFormHelperText-root': { margin: 0 } }}
              {...register(
                'district',

                {
                  required: `District / Country is required`,
                  maxLength: { value: 70, message: 'Address cannot exceed 70 characters' },
                }
              )}
              error={errors && !!errors?.district}
              helperText={errors?.district?.message}
            /> */}

            <Select
              register={register}
              field={{ ...teachersOnboardingTextFields[10], label: 'state', name: 'state' }}
              errors={errors}
              name={'state'}
              watch={watch}
              defaultValue={watch('state') || ''}
              onChange={(e) => setValue('state', e.target.value)}
              items={teachersOnboardingTextFields[10].items}
              schoolDistrict
            />

            {/*
            <TextField
              name="district"
              label="District"
              placeholder="District Here"
              fullWidth
              // {...register('confirmPassword')}
              type="text"
              sx={{ mb: '17px', '& .MuiFormHelperText-root': { margin: 0 } }}
              {...register(
                'district',

                {
                  required: `District is required`,
                  maxLength: { value: 30, message: 'District Name cannot exceed 30 characters' },
                }
              )}
              error={errors && !!errors?.district}
              helperText={errors?.district?.message}
            /> */}
          </DialogContent>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              px: 3,
              py: 2.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
              flexWrap: 'wrap',
            }}
          >
            <Button variant="outlined" color="secondary" onClick={handleClose} sx={{ minWidth: 140, height: 44 }}>
              Cancel
            </Button>
            <LoadingButton
              loading={isLoading}
              variant="contained"
              color="main"
              type="submit"
              sx={{ minWidth: 160, height: 44 }}
            >
              Save
            </LoadingButton>
          </Box>
        </form>
      </Dialog>
    </div>
  );
}
